import { useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { apiFetch, isApiError } from '../api/client'
import { useSession } from '../context/SessionContext'
import { safeNext, withNext } from '../context/pendingInvite'
import { AuthCard } from '../components/AuthCard'

type LoginState = 'idle' | 'loading' | 'error'

export function LoginView() {
  const { recheck } = useSession()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [state, setState] = useState<LoginState>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const [params] = useSearchParams()
  // `?next=` (e.g. back to an invite) survives the switch to sign-up / forgot password;
  // AppGate sends a signed-in user there.
  const next = safeNext(params.get('next'))
  const justReset = params.get('reset') === '1'
  const passwordSet = params.get('password_set') === '1'

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setState('loading')
    setErrorMsg('')
    try {
      // Sets symone_session + symone_refresh (httpOnly) and symone_csrf.
      await apiFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      })
      recheck()
    } catch (err) {
      setState('error')
      const s = isApiError(err) ? err.status : 0
      if (s === 400 || s === 401 || s === 422) setErrorMsg('Invalid email or password.')
      else if (s === 429) setErrorMsg('Too many attempts. Wait a minute and try again.')
      else setErrorMsg('Something went wrong, please try again.')
    }
  }

  return (
    <AuthCard
      title="Sign in"
      subtitle="Welcome back. Sign in to your developer dashboard."
      footer={
        <>
          <Link to={withNext('/forgot-password', next)}>Forgot password?</Link>
          <span>
            No account? <Link to={withNext('/signup', next)}>Sign up</Link>
          </span>
        </>
      }
    >
      {justReset && state !== 'error' && (
        <p role="status" className="form-notice">
          Password updated and every session signed out. Sign in with your new password.
        </p>
      )}
      {passwordSet && state !== 'error' && (
        <p role="status" className="form-notice">
          Password set — sign in to accept the invite.
        </p>
      )}
      <form onSubmit={handleSubmit} aria-label="Sign in form" noValidate>
        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={state === 'loading'}
            required
            aria-describedby={state === 'error' ? 'login-error' : undefined}
          />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={state === 'loading'}
            required
          />
        </div>
        {state === 'error' && (
          <p id="login-error" role="alert" className="form-error">{errorMsg}</p>
        )}
        <button
          type="submit"
          className="btn btn--primary btn--block"
          disabled={state === 'loading' || !email || !password}
          aria-busy={state === 'loading'}
        >
          {state === 'loading' ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </AuthCard>
  )
}
